/**
 * localStorage-backed eligibility store for the Rovo Chat CSAT survey.
 *
 * Eligibility requires:
 *   1. 30-day hard cooldown since the last submit/dismiss (`lastInteractedAt`).
 *   2. 7-day soft cooldown since the last shown (`lastShownAt`) — prevents the
 *      survey from re-appearing on every response when the user ignores it.
 *   3. At least 3 qualifying new conversations since the last show/interaction.
 */

import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls';

const STORAGE_KEY = 'rovoChatCsatEligibility';
const HARD_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
const SOFT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const MIN_QUALIFYING_CONVERSATIONS = 3;

type CsatEligibilityData = {
	lastShownAt: number;
	lastInteractedAt: number;
	qualifyingConversations: number;
};

const DEFAULTS: CsatEligibilityData = {
	lastShownAt: 0,
	lastInteractedAt: 0,
	qualifyingConversations: 0,
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCsatEligibilityData(value: unknown): value is CsatEligibilityData {
	return (
		isPlainObject(value) &&
		typeof value.lastShownAt === 'number' &&
		typeof value.lastInteractedAt === 'number' &&
		typeof value.qualifyingConversations === 'number'
	);
}

function readFromStorage(): CsatEligibilityData {
	try {
		const raw = AtlBrowserStorageLocal.getItem(STORAGE_KEY);
		if (raw) {
			const parsed: unknown = JSON.parse(raw);
			if (isCsatEligibilityData(parsed)) {
				return parsed;
			}
		}
	} catch {
		// Storage unavailable or JSON corrupt — fall through to defaults.
	}
	return DEFAULTS;
}

function writeToStorage(data: CsatEligibilityData): void {
	try {
		AtlBrowserStorageLocal.setItem(STORAGE_KEY, JSON.stringify(data));
	} catch {
		// Best-effort: storage might be denied (no consent) or full.
	}
}

function isInCooldown(data: CsatEligibilityData): boolean {
	const now = Date.now();
	if (data.lastInteractedAt && now - data.lastInteractedAt < HARD_COOLDOWN_MS) {
		return true;
	}
	if (data.lastShownAt && now - data.lastShownAt < SOFT_COOLDOWN_MS) {
		return true;
	}
	return false;
}

// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const isCsatEligible = (): boolean => {
	const data = readFromStorage();
	return !isInCooldown(data) && data.qualifyingConversations >= MIN_QUALIFYING_CONVERSATIONS;
};

/**
 * Call when the user sends the first valid message of a new conversation.
 * No-op during either cooldown window: the "3 qualifying conversations" gate
 * must demonstrate engagement AFTER the cooldown ends, not during it.
 */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const recordValidMessage = (): void => {
	const cur = readFromStorage();
	if (isInCooldown(cur)) {
		return;
	}
	if (cur.qualifyingConversations >= MIN_QUALIFYING_CONVERSATIONS) {
		return;
	}
	writeToStorage({
		...cur,
		qualifyingConversations: cur.qualifyingConversations + 1,
	});
};

/** Call when the survey is displayed. Starts the 7-day soft cooldown. */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const recordCsatDisplayed = (): void => {
	const cur = readFromStorage();
	writeToStorage({
		...cur,
		lastShownAt: Date.now(),
		qualifyingConversations: 0,
	});
};

/** Call when the user submits or dismisses the survey. Starts the 30-day hard cooldown. */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const recordCsatInteracted = (): void => {
	const now = Date.now();
	writeToStorage({
		lastShownAt: now,
		lastInteractedAt: now,
		qualifyingConversations: 0,
	});
};

/** Test/debug helper: wipe the persisted eligibility record. */
// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file
export const clearCsatEligibility = (): void => {
	try {
		AtlBrowserStorageLocal.removeItem(STORAGE_KEY);
	} catch {
		// Best-effort.
	}
};
